import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import Croppie from 'croppie';
import _uniqueId from 'lodash/uniqueId';

import './croppie.css';
import StyledInputImageCrop from './styles';

import iconEdit from '../../assets/icons/edit.svg';
import iconCamera from '../../assets/icons/camera.svg';
import iconCrop from '../../assets/icons/crop.svg';
import iconRotate from '../../assets/icons/rotate.svg';

import iconLoading from '../../assets/icons/loading-alt.svg';
import iconSuccess from '../../assets/icons/check-circle.svg';
import iconError from '../../assets/icons/times-circle.svg';

import Button, {buttonStyle} from '../Button';
import uploadFile from '../InputFileUpload/uploader';
import useStateCallback from '../../helpers/useStateCallback'


const ButtonStyledDiv = styled.div`
    ${buttonStyle}
`;

interface props {
    name: string;
    config: {
        width: number,
        height: number,
        output: {
            width: number,
            height: number,
        }
    }
}

const Component = React.forwardRef((props:props, ref) => {
    const [state, setState] = useState('step-one');
    const [uploadState, setUploadState] = useState('idle');
    const [uploadStatus, setUploadStatus] = useState({});

    const [croppieInstance, setCroppieInstance] = useStateCallback(null);
    const [sourceFile, setSourceFile] = useState(undefined);
    const [targetBlob, setTargetBlob] = useState(undefined);
    const [targetFile, setTargetFile] = useState(undefined);

    const [inputLabelId] = useState(_uniqueId('crop-file-input-'));

    const targetInput = useRef();
    const croppieElement = useRef();
    const inputElement = useRef();

    useEffect(() => {
        if (sourceFile) {
            createAndBindCroppieInstance();
        }
    }, [sourceFile]);

    useEffect(() => {
        determineCurrentState();
    }, [sourceFile, targetFile]);

    useEffect(() => {
        console.log(targetBlob);
    }, [targetBlob]);

    useEffect(() => {
        if (targetFile) {
            handleFileUpload(targetFile);
        }
    }, [targetFile]);

    useEffect(() => {
        if (uploadStatus.key) {
            targetInput.current.querySelector('.targetInput').value = uploadStatus.key;
        }
    }, [uploadStatus])

    const determineCurrentState =  () => {
        let currentState = 'step-one';
        if (sourceFile) {
            currentState = 'step-two'
        }
        if (targetFile) {
            currentState = 'step-three'
        }
        setState(currentState);
    }

    const handleInputChange = (e) => {
        const inputFile = e.target.files[0];
        if (inputFile) {
            setSourceFile(URL.createObjectURL(inputFile));
        }
    }

    const handleFileUpload = async (file:File)=> {
        setUploadState('uploading');
        uploadFile(file).then(r => {
            setUploadStatus(r);
            setUploadState('uploaded');
        }).catch( e => {
            setTargetFile(undefined);
            setUploadState('error');
        });
    }

    const croppieOptions = {
        enableExif: true,
        showZoomer: true,
        enableZoom:true,
        enforceBoundary:false,
        enableOrientation: true,
        viewport: {
            width: props.config.width,
            height: props.config.height,
        },
        boundary: {
            width: props.config.width,
            height: props.config.height,
        }
    };

    const createAndBindCroppieInstance =  () => {
        if (croppieInstance !== null) {
            croppieInstance.destroy();
            setCroppieInstance(null);
        }
        let newCroppieInstance = new Croppie(croppieElement.current, croppieOptions);
        setCroppieInstance(
            newCroppieInstance,
            (newValue) => {
                newValue.bind({
                    url: sourceFile,
                }).catch(() => {
                    setState('has-error');
                });
            }
        );
    }

    const handleCrop = () => {
        if (croppieInstance !== null) {
            croppieInstance.result(
                {
                    format:'jpeg',
                    type: 'blob',
                    backgroundColor: '#FFFFFF',
                    size: {
                        width: props.config.output.width || props.config.width,
                        height: props.config.output.height || props.config.height,
                    },
                })
                .then(blob => {
                    // Convert blob to base64 and store it for preview
                    var reader = new FileReader();
                    reader.readAsDataURL(blob); 
                    reader.onloadend = function() {
                        setTargetBlob(reader.result);
                    }

                    // Convert blob to file
                    const file = new File([blob], 'photo.jpg', {type:'image/jpeg', lastModified:new Date().getTime()})
                    setTargetFile(file);
                });
        }
    }
    const handleRotate = () => {
        if (croppieInstance !== null) 
            croppieInstance.rotate(90);
    }


    const handleUpdateCrop = () => {
        setUploadState('idle')
        setTargetFile(undefined)
    }

    console.log(state);

    return (
        <StyledInputImageCrop className={state} config={props.config}>
            <input type="text" name={props.name} className="hidden-input" ref={ref}/>

            <div className='select-and-crop-wrapper'>
                <label className='file-input-wrapper' htmlFor={inputLabelId} ref={targetInput}>
                    <ButtonStyledDiv className='with-icon' icon={iconCamera}>{!sourceFile? 'Seleccionar fotografía':'Seleccionar otra fotografía'}</ButtonStyledDiv>
                    <input accept="image/*" id={inputLabelId} type="file" onChange={(e) => handleInputChange(e)} ref={inputElement}/>
                    <input type="text" className='targetInput' name={props.name} ref={ref}/>
                </label>
                <div className="error-message">Ocurrió un error con la fotografía que seleccionó. Es posible que el archivo se encuentre dañado. Intente seleccionar otro archivo.</div>
                <div className="cropper-element" ref={croppieElement}></div>
                <div>
                    {state==='step-two'?
                        <Button className="crop-rotate" type="button" icon={iconRotate} onClick={handleRotate} style={{marginRight:'5px',marginBottom:'5px'}}>Rotar</Button>
                        :''}
                    <Button className="crop-button" type="button" icon={iconCrop} onClick={handleCrop}>Guardar foto</Button>
                </div>
            </div>
            <div className="preview-wrapper">
                <img src={targetBlob} style={{width:props.config.width,height:props.config.height}} />
                <div>
                    <Button type="button" icon={iconEdit} onClick={() => handleUpdateCrop()}>Modificar recorte</Button>
                </div>
            </div>
            {
                uploadState === 'uploading' &&
                <div className="status status--uploading">
                    <div className="label">Cargando archivo </div>
                    <div className="icon"><img src={iconLoading} alt="Cargando"/></div>
                </div>
            }
            {
                uploadState === 'uploaded' &&
                <div className="status status--uploaded">
                    <div className="label">El archivo se cargó exitosamente</div>
                    <div className="icon"><img src={iconSuccess}/></div>
                </div>
            }
            {
                uploadState === 'error' &&
                <div className="status status--error">
                    <div className="label">Ocurrió un error al intentar subir el archivo. Intente nuevamente.</div>
                    <div className="icon"><img src={iconError}/></div>
                </div>
            }
        </StyledInputImageCrop>
    )
});

export default Component;